<template>
  <section class="view-stack overview-page">
    <div class="overview-toolbar">
      <div class="overview-toolbar-actions">
        <el-radio-group v-model="range" size="small" @change="loadOverview">
          <el-radio-button label="today">今天</el-radio-button>
          <el-radio-button label="week">近 7 天</el-radio-button>
          <el-radio-button label="month">近 30 天</el-radio-button>
        </el-radio-group>
        <el-button
          plain
          icon="el-icon-refresh"
          :loading="loading"
          @click="loadOverview"
        >刷新</el-button>
      </div>
    </div>

    <section class="dashboard-stats">
      <article v-for="item in stats" :key="item.label" class="stat-card">
        <div class="stat-icon" :class="item.tone">
          <i :class="item.icon"></i>
        </div>
        <span>{{ item.label }}</span>
        <strong>{{ item.value }}</strong>
        <small>{{ item.note }}</small>
      </article>
    </section>

    <section class="dashboard-grid">
      <article class="dashboard-panel platform-panel">
        <div class="panel-heading">
          <div>
            <h2>模型使用分布</h2>
            <p>{{ rangeLabel }}内各模型的请求量占比。</p>
          </div>
          <strong class="panel-total">{{ formatNumber(summary.totalRequests) }} 次请求</strong>
        </div>
        <div v-if="modelStats.length" class="platform-list">
          <div v-for="item in modelStats" :key="item.model" class="platform-row">
            <div>
              <strong>{{ item.model }}</strong>
              <span>{{ formatCompactNumber(item.totalTokens) }} Token · {{ formatAmount(item.amount) }}</span>
            </div>
            <el-progress :percentage="modelPercent(item)" :show-text="false" color="#1ec7a6"></el-progress>
            <b>{{ modelPercent(item) }}%</b>
          </div>
        </div>
        <div v-else class="overview-empty">当前时间范围内暂无调用记录</div>
      </article>

      <article class="dashboard-panel actions-panel">
        <div class="panel-heading compact-heading">
          <h2>常用入口</h2>
        </div>
        <button
          v-for="item in quickActions"
          :key="item.label"
          type="button"
          class="quick-action"
          @click="$router.push({ name: item.target })"
        >
          <i :class="item.icon"></i>
          <span>{{ item.label }}</span>
        </button>
      </article>

      <article class="dashboard-panel model-panel">
        <div class="panel-heading">
          <div>
            <h2>模型排行</h2>
            <p>按请求量排序，展示当前时间范围内的真实统计。</p>
          </div>
        </div>
        <el-table v-if="modelStats.length" :data="modelStats" class="dashboard-table" size="small">
          <el-table-column prop="model" label="模型" min-width="140" show-overflow-tooltip></el-table-column>
          <el-table-column label="请求" width="90" align="right">
            <template slot-scope="scope">{{ formatNumber(scope.row.requests) }}</template>
          </el-table-column>
          <el-table-column label="Token" width="120" align="right">
            <template slot-scope="scope">{{ formatCompactNumber(scope.row.totalTokens) }}</template>
          </el-table-column>
          <el-table-column label="消费" width="110" align="right">
            <template slot-scope="scope">{{ formatAmount(scope.row.amount) }}</template>
          </el-table-column>
        </el-table>
        <div v-else class="overview-empty">暂无模型统计</div>
      </article>

      <article class="dashboard-panel trend-panel">
        <div class="panel-heading">
          <div>
            <h2>请求趋势</h2>
            <p>按天汇总请求量，柱高代表当天请求量。</p>
          </div>
        </div>
        <div v-if="trendData.length" class="trend-chart">
          <div
            v-for="item in trendData"
            :key="item.day"
            class="trend-bar"
            :title="trendTitle(item)"
          >
            <span :style="{ height: trendHeight(item) + '%' }"></span>
            <small>{{ shortDay(item.day) }}</small>
          </div>
        </div>
        <div v-else class="overview-empty">当前时间范围内暂无趋势数据</div>
      </article>

    </section>
  </section>
</template>

<script>
import { fetchDashboardOverview } from '../../api/dashboard';

function createOverview() {
  return {
    summary: {
      totalRequests: 0,
      successRequests: 0,
      issueRequests: 0,
      inputTokens: 0,
      outputTokens: 0,
      cachedInputTokens: 0,
      cacheCreationInputTokens: 0,
      totalTokens: 0,
      totalAmount: 0
    },
    apiKeyCount: 0,
    modelStats: [],
    trend: [],
    recentRecords: []
  };
}

export default {
  name: 'OverviewView',
  data() {
    return {
      loading: false,
      range: 'today',
      overview: createOverview(),
      quickActions: [
        { label: '管理 API Key', icon: 'el-icon-key', target: 'dashboardKeys' },
        { label: '查看使用记录', icon: 'el-icon-tickets', target: 'dashboardUsage' },
        { label: '查看余额分组', icon: 'el-icon-wallet', target: 'dashboardSubscription' },
        { label: '充值与账单', icon: 'el-icon-document', target: 'dashboardBilling' }
      ]
    };
  },
  computed: {
    summary() {
      return this.overview.summary || createOverview().summary;
    },
    modelStats() {
      return Array.isArray(this.overview.modelStats) ? this.overview.modelStats : [];
    },
    trendData() {
      return Array.isArray(this.overview.trend) ? this.overview.trend : [];
    },
    rangeLabel() {
      return { today: '今天', week: '近 7 天', month: '近 30 天' }[this.range] || '当前范围';
    },
    stats() {
      const totalRequests = Number(this.summary.totalRequests || 0);
      return [
        {
          label: '有效 API Key',
          value: this.formatNumber(this.overview.apiKeyCount),
          note: '当前账户可用密钥',
          icon: 'el-icon-key',
          tone: 'blue'
        },
        {
          label: this.rangeLabel + '请求',
          value: this.formatNumber(totalRequests),
          note: '仅统计成功记录',
          icon: 'el-icon-s-data',
          tone: 'green'
        },
        {
          label: this.rangeLabel + '消费',
          value: this.formatAmount(this.summary.totalAmount),
          note: '按账户费率计算',
          icon: 'el-icon-money',
          tone: 'orange'
        },
        {
          label: this.rangeLabel + ' Token',
          value: this.formatCompactNumber(this.summary.totalTokens),
          note: '输入、读缓存、写缓存和输出合计',
          icon: 'el-icon-coin',
          tone: 'violet'
        }
      ];
    }
  },
  created() {
    this.loadOverview();
  },
  methods: {
    async loadOverview() {
      this.loading = true;
      try {
        const { data } = await fetchDashboardOverview(this.buildRangeParams());
        if (!data || data.code !== 0 || !data.data) {
          throw new Error((data && data.message) || 'DASHBOARD_LOAD_FAILED');
        }
        this.overview = Object.assign(createOverview(), data.data);
      } catch (error) {
        this.overview = createOverview();
        this.$message.error(error.message || '仪表盘数据加载失败');
      } finally {
        this.loading = false;
      }
    },
    buildRangeParams() {
      const end = new Date();
      const start = new Date(end.getTime());
      if (this.range === 'today') {
        start.setHours(0, 0, 0, 0);
      } else if (this.range === 'week') {
        start.setDate(start.getDate() - 6);
        start.setHours(0, 0, 0, 0);
      } else {
        start.setDate(start.getDate() - 29);
        start.setHours(0, 0, 0, 0);
      }
      return {
        startTime: this.formatDateTimeValue(start),
        endTime: this.formatDateTimeValue(end),
        status: 'SUCCESS'
      };
    },
    modelPercent(item) {
      const total = Number(this.summary.totalRequests || 0);
      if (!total) {
        return 0;
      }
      return Math.round(Number(item.requests || 0) / total * 100);
    },
    trendHeight(item) {
      const max = this.trendData.reduce((current, value) => Math.max(current, Number(value.requests || 0)), 0);
      if (!max) {
        return 8;
      }
      return Math.max(8, Math.round(Number(item.requests || 0) / max * 100));
    },
    trendTitle(item) {
      return item.day + '：' + this.formatNumber(item.requests) + ' 次请求，' + this.formatAmount(item.amount);
    },
    shortDay(day) {
      const value = String(day || '');
      return value.length >= 10 ? value.slice(5) : value;
    },
    formatAmount(value) {
      return '$' + Number(value || 0).toFixed(4);
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString('zh-CN');
    },
    formatCompactNumber(value) {
      const number = Number(value || 0);
      if (number >= 1000000) {
        return (number / 1000000).toFixed(2) + 'M';
      }
      if (number >= 1000) {
        return (number / 1000).toFixed(1) + 'K';
      }
      return String(number);
    },
    formatDateTimeValue(date) {
      const pad = value => String(value).padStart(2, '0');
      return date.getFullYear()
        + '-' + pad(date.getMonth() + 1)
        + '-' + pad(date.getDate())
        + ' ' + pad(date.getHours())
        + ':' + pad(date.getMinutes())
        + ':' + pad(date.getSeconds());
    }
  }
};
</script>
